import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import { 
  CSATHome, 
  CSATPracticeMode, 
  CSATMockTest,
  CSATQuestionSolver,
  CSATRCPractice,
  CSATAnalytics,
  CSATPYQAnalysis,
  CSATProgress,
  CSATLearnMode,
  CSATCurriculum,
  CSATUniversalPractice,
  CSATTestSeries,
  SITestSeries,
  CSATConceptNotes
} from "@/components/csat";
import { useExam } from "@/context/ExamContext";
import { UniversalLearn, UniversalAnalyticsDashboard } from "@/components/universal";

const CSATSuitePage = () => {
  const { user } = useAuth();
  const { isSI } = useExam();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState("home");
  
  // Shared state for components that need to pass data between modes
  const [question, setQuestion] = useState("");
  const [questionType, setQuestionType] = useState("quant");
  const [learningTopics, setLearningTopics] = useState([]);
  const [practiceSubTopic, setPracticeSubTopic] = useState(null); // For topic-wise practice from Learn mode

  // Honor ?mode=...&topic=... deep-links (e.g. from Strategy Dashboard
  // "Practice this 5 Qs" CTAs). Reads once on mount.
  useEffect(() => {
    const urlMode = searchParams.get("mode");
    const urlTopic = searchParams.get("topic");
    if (urlTopic) setPracticeSubTopic(urlTopic);
    if (urlMode) {
      setMode(urlMode);
      // Strip params so a "Back to home" inside the page doesn't keep
      // re-triggering this effect on remount.
      const next = new URLSearchParams(searchParams);
      next.delete("mode");
      next.delete("topic");
      setSearchParams(next, { replace: true });
    }
  }, []); // run once on mount only

  // Exam-scoped PRO. SI is isolated — only an "SI" entitlement unlocks SI PRO;
  // UPSC PRO (global role) does NOT unlock SI, and vice-versa.
  const proExams = user?.pro_exams || [];
  const isAdmin = user?.role === "ADMIN" || user?.is_admin;
  const isPro = isAdmin || (isSI ? proExams.includes("SI") : (user?.role === "PRO" || proExams.includes("UPSC")));
  const examCtx = isSI ? "SI" : "UPSC";
  
  // Track activity when mode changes
  const handleSetMode = async (newMode) => {
    setMode(newMode);
    
    // Track activity for "Continue where you left off"
    if (newMode !== "home") {
      const modeNames = {
        "practice": "CSAT Practice",
        "universal-practice": "CSAT Universal Practice",
        "test-series": "CSAT Timed Test Series",
        "si-test-series": "SI Mains Test Series",
        "learn": "CSAT Learn",
        "mock-test": "CSAT Mock Test",
        "solve": "CSAT Question Solver",
        "rc-practice": "CSAT RC Practice",
        "analytics": "CSAT Analytics",
        "pyq-analysis": "CSAT PYQ Analysis",
        "progress": "CSAT Progress",
        "curriculum": "CSAT Curriculum"
      };
      
      try {
        const token = localStorage.getItem("token");
        await managedAxios.post(
          `${API_BASE}/command/track-activity?activity_type=csat&activity_name=${encodeURIComponent(modeNames[newMode] || "CSAT Suite")}&activity_path=/csat-suite`,
          null,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        // Silently fail - tracking is non-critical
      }
    }
  };

  // HOME VIEW
  if (mode === "home") {
    return <CSATHome isPro={isPro} setMode={handleSetMode} isSI={isSI} />;
  }

  // PRACTICE MODE - Use extracted component
  if (mode === "practice") {
    return (
      <CSATPracticeMode
        isPro={isPro}
        onBack={() => setMode("home")}
        learningTopics={learningTopics}
        setLearningTopics={setLearningTopics}
      />
    );
  }

  // UNIVERSAL PRACTICE MODE - NEW: High-quality MCQ bank
  if (mode === "universal-practice") {
    return (
      <CSATUniversalPractice
        isPro={isPro}
        exam={examCtx}
        onBack={() => {
          setMode("home");
          setPracticeSubTopic(null); // Clear sub-topic filter
        }}
        initialSubTopic={practiceSubTopic}
      />
    );
  }

  // TIMED TEST SERIES - NEW: 80-Q, 120-min full-length CSAT simulator (PRO)
  if (mode === "test-series") {
    return (
      <CSATTestSeries
        isPro={isPro}
        onBack={() => setMode("home")}
      />
    );
  }

  // SI MAINS TEST SERIES - NEW: 200-Q, 180-min papers (PRO)
  if (mode === "si-test-series") {
    return (
      <SITestSeries
        isPro={isPro}
        onBack={() => setMode("home")}
      />
    );
  }

  // LEARN MODE - Curated CSAT concept notes (uploaded chapter reference)
  if (mode === "learn") {
    return (
      <CSATConceptNotes
        onBack={() => setMode("home")}
        onPractice={(chapter) => {
          setPracticeSubTopic(chapter);
          setMode("universal-practice");
        }}
      />
    );
  }

  // MOCK TEST - Use extracted component
  if (mode === "mock-test" && isPro) {
    return (
      <CSATMockTest
        isPro={isPro}
        onBack={() => setMode("home")}
      />
    );
  }

  // QUESTION SOLVER - Use extracted component
  if (mode === "solve") {
    return (
      <CSATQuestionSolver
        isPro={isPro}
        onBack={() => setMode("home")}
        initialQuestion={question}
        initialType={questionType}
      />
    );
  }

  // RC PRACTICE - Use extracted component
  if (mode === "rc-practice") {
    return (
      <CSATRCPractice
        isPro={isPro}
        onBack={() => setMode("home")}
        onSolveRC={(passage) => {
          setQuestion(passage);
          setQuestionType("rc");
          setMode("solve");
        }}
      />
    );
  }


  // ANALYTICS - Use Universal Analytics Dashboard
  if (mode === "analytics") {
    return (
      <UniversalAnalyticsDashboard 
        onBack={() => setMode("home")}
        onPractice={(topic) => {
          if (topic) {
            setPracticeSubTopic(topic);
          }
          setMode("universal-practice");
        }}
      />
    );
  }


  // PYQ ANALYSIS - Use extracted component
  if (mode === "pyq-analysis") {
    return (
      <CSATPYQAnalysis
        isPro={isPro}
        onBack={() => setMode("home")}
      />
    );
  }


  // LEARN MODE - Use extracted component
  if (mode === "learn") {
    return (
      <CSATLearnMode
        isPro={isPro}
        onBack={() => setMode("home")}
        learningTopics={learningTopics}
        setLearningTopics={setLearningTopics}
      />
    );
  }


  // CURRICULUM MODE - Use extracted component
  if (mode === "curriculum") {
    return (
      <CSATCurriculum
        isPro={isPro}
        onBack={() => setMode("home")}
      />
    );
  }

  // PROGRESS - Use extracted component
  if (mode === "progress") {
    return (
      <CSATProgress
        isPro={isPro}
        onBack={() => setMode("home")}
      />
    );
  }


  return null;
};

export default CSATSuitePage;
